<script setup lang="ts">
type StudentUserData = App.Auth.Data.StudentUserData;
type StudentEmailVerificationData = App.StudentApp.Data.StudentEmailVerificationData;

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import CircleIcon from '@/components/circleIcon/CircleIcon.vue';
import Dialog from '@/components/dialog/Dialog.vue';

import EnvelopeCheck from '@/icons/line/envelope-check.svg';

const props = defineProps<{
  user: StudentUserData;
  emailVerification: StudentEmailVerificationData & { latest_sent_at?: string };
}>();

const { show, closeLocally } = useDialog();

const form = useForm({
  url: route('student.profile.email.update'),
  method: 'PUT',
  fields: {
    email: props.user.email
  }
});

const resendForm = useForm({
  url: route('student.profile.email-verify.resend'),
  method: 'POST',
  fields: {}
});

const intervalTimer = ref<NodeJS.Timeout | null>(null);
const timeRemaining = ref(0);
const linkJustSent = ref(false);
const linkSentTimer = ref<NodeJS.Timeout | null>(null);

const formattedTimeRemaining = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60);
  const seconds = timeRemaining.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

function calculateTimeRemaining(): number {
  if (!props.emailVerification?.latest_sent_at) {
    return 0;
  }

  const sentAt = new Date(props.emailVerification.latest_sent_at);
  const expiresAt = new Date(sentAt.getTime() + 30 * 1000); // 30 seconds in milliseconds
  const now = new Date();

  return Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
}

function startCountdown() {
  timeRemaining.value = calculateTimeRemaining();

  if (timeRemaining.value <= 0) {
    return;
  }

  intervalTimer.value = setInterval(() => {
    timeRemaining.value = calculateTimeRemaining();

    if (timeRemaining.value <= 0) {
      clearInterval(intervalTimer.value!);
    }
  }, 1000);
}

function stopCountdown() {
  if (intervalTimer.value) {
    clearInterval(intervalTimer.value);
    intervalTimer.value = null;
  }
}

function stopLinkSentTimer() {
  if (linkSentTimer.value) {
    clearTimeout(linkSentTimer.value);
    linkSentTimer.value = null;
  }
}

onMounted(() => {
  if (props.emailVerification?.latest_sent_at) {
    startCountdown();
  }
});

onUnmounted(() => {
  stopCountdown();
  stopLinkSentTimer();
});

const buttonLabel = computed(() => {
  if (linkJustSent.value) {
    return 'Link sent!';
  }
  return timeRemaining.value > 0 ? `Resend link in ${formattedTimeRemaining.value}` : 'Resend link';
});

const buttonColor = computed(() => {
  return linkJustSent.value ? ButtonColor.success : ButtonColor.slate;
});

const isButtonDisabled = computed(() => {
  return linkJustSent.value || timeRemaining.value > 0;
});

const handleResend = () => {
  resendForm.submitWith({
    url: route('student.profile.email-verify.resend'),
    hooks: {
      success() {
        // Update the verification timestamp locally for immediate UI feedback
        if (props.emailVerification) {
          props.emailVerification.latest_sent_at = new Date().toISOString();
          stopCountdown();
          startCountdown();

          linkJustSent.value = true;
          stopLinkSentTimer();
          linkSentTimer.value = setTimeout(() => {
            linkJustSent.value = false;
          }, 5000);
        }
      }
    }
  });
};
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Email"
    :isOpen="show"
    :isLoading="form.processing"
    isStacked
    hideFooter
    @onClose="closeLocally"
  >
    <template #form>
      <div class="flex flex-col items-center gap-y-8">
        <div class="flex flex-col items-center gap-y-7 pt-4">
          <CircleIcon :icon="EnvelopeCheck" />
          <div class="flex flex-col items-center gap-y-2.5">
            <h2 class="text-center text-xl font-semibold leading-7 text-slate-900">
              Check your inbox
            </h2>
            <p class="text-center text-md font-normal leading-relaxed text-slate-700">
              We've sent an email verification link to
              <strong class="font-bold">{{ emailVerification?.new_email }}</strong
              >.
            </p>
          </div>
        </div>
        <div class="flex w-full flex-col items-stretch justify-stretch gap-y-3">
          <Button
            @click="closeLocally"
            :color="ButtonColor.slate"
            :size="ButtonSize.lg"
            :variant="ButtonVariant.soft"
            >Close</Button
          >
          <Button
            :color="buttonColor"
            :size="ButtonSize.lg"
            :variant="ButtonVariant.invisible"
            :isLoading="resendForm.processing"
            :disabled="isButtonDisabled"
            @click="handleResend"
          >
            {{ buttonLabel }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>
